// Imports ------------------------------------------------------------------->

import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {Dialog} from "../flipp-dialog/Dialog";
import {i18n} from "../../i18n/I18n";
import {I18nSpan} from "../flipp-i18n-span/I18nSpan";
import {icons} from "../../icons/Icons";

import {inject} from "../../services/di";
import {LocationService, Country, PostalCode} from "../../services/LocationService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {UserAgentService} from "../../services/UserAgentService";
import {StorageService} from "../../services/StorageService";
import {LinkService} from "../../services/LinkService";
import {ConfigService} from "../../services/ConfigService";
import {LandingPageActions} from "../../services/definitions/IAnalyticsService";
import {isCrawler} from "../../utils/crawler";
import {MobileBanner} from "../flipp-mobile-banner/MobileBanner";
import {FlippCitiesFooterLinks} from "../flipp-cities-footer-links/FlippCitiesFooterLinks";
import { SeoService } from "../../services/SeoService";
import {BlackFridayService} from "../../services/BlackFridayService";

// Private Definitions ------------------------------------------------------->
const FLIPP_CORP_BASEPATH = "https://corp.flipp.com";

// Public Definitions -------------------------------------------------------->

interface LandingPageProperties extends JSX.HTMLProps {
	cityName: string;
}

@element("flipp-new-landing-page")
export class NewLandingPage extends Component<LandingPageProperties, void> {

	refs: {
		postalCodeInput: HTMLInputElement,
		postalCodeForm: HTMLFormElement,
		postalCodeWrapper: HTMLDivElement,
		searchInput: HTMLInputElement
	};

	@attr cityName: string;
	@inject location: LocationService;
	@inject userAgent: UserAgentService;
	@inject storage: StorageService;
	@inject analytics: AnalyticsService;
	@inject config: ConfigService;
	@inject seoService: SeoService;
	@inject linkService: LinkService;
	@inject bfService: BlackFridayService;

	private navDialog: Dialog;

	render() {
		/* 'postalcode' attribute is set on the document element and exposed via
			* `this.postalCode`. This value is a *guess* based on IP */
		const year = new Date().getFullYear();

		const country = this.location.country;
		const isFrench = this.location.acceptsLocale("fr");
		const lang = this.location.locale.split("-")[0];
		const countrySuffix = country === Country.CANADA ? "ca" : "us";
		const langSuffix = isFrench ? "-fr" : "";
		const hostname = `${window.location.protocol}//${this.config.HOSTNAME}`;

		const retailerLogosObj = {
			"ca": ["walmart", "nofrills", "homedepot", "petsmart", "toysrus", "canadiantire", isFrench ? "shoppers-fr" : "shoppers", "sobeys"],
			"us": ["walmart", "kroger", "homedepot", "cvs", "toysrus", "meijer", "familydollar", "dicks"]
		};

		const cpgLogosArray   = ["CVS", "FamilyDollar", "Meijer", "Publix", "RiteAid", "Walgreens"];

		const primaryNav = (
			<div class="primary-nav">
				<button class="hash-link" onClick={this._handleFlyersNavClick}>{i18n("flyers_header_category")}</button>
				<button class="hash-link" onClick={this._handleCouponsNavClick}>{i18n("coupons")}</button>
				<button class="hash-link" onClick={this._handleSLNavClick}>{i18n("shopping_list")}</button>
				<button class="hash-link" onClick={this._handleSearchNavClick}>{i18n("search")}</button>
			</div>
		);

		const secondaryNav = this._getSecondaryNav();
		const headerBlock = (
			<header>
				<div class="flipp-logo">
					<a href={FLIPP_CORP_BASEPATH} target="_blank" onClick={this._handleAboutFlippClick.bind(this, "Nav Logo")}>
						{icons.flippLogo}
					</a>
				</div>
				<nav>
					{primaryNav}
					{secondaryNav}
				</nav>
				<button class="nav-hamburger" title={i18n("open_navigation_menu")} aria-label={i18n("open_navigation_menu")}
					onClick={this._handleHamburgerClick}>
					<div>{icons.hamburger}</div>
				</button>
			</header>
		);

		const retailerLogos = (
			<div class="logos">
				{retailerLogosObj[countrySuffix].map((name) =>
					<div>
						<img class="retailer" classList={{"us": country !== Country.CANADA}}
							src={`https://f.wishabi.net/creative/Distribution/images/landing/logos/retailer-logos/${countrySuffix}/logo-${name}.png`} alt={`logo of ${name} retailer`} />
					</div>
				)}
			</div>
		);

		const cpgLogos = (
			<div class="logos">
				{cpgLogosArray.map((name) =>
					<div>
						<img src={`https://f.wishabi.net/creative/Distribution/images/homepage/${name}.png`} alt={`logo of ${name} brand`} />
					</div>
				)}
			</div>
		);

		const happyFruit = (
			<div class="happy-fruit">
				<div>
					<img class="myMilk" src="https://f.wishabi.net/creative/Distribution/images/icons/myMilk.svg" alt=""/>
				</div>
				<div>
					<img class="myEggs" src="https://f.wishabi.net/creative/Distribution/images/icons/myEggs.svg" alt=""/>
				</div>
				<div>
					<img class="myButter" src="https://f.wishabi.net/creative/Distribution/images/icons/myButter.svg" alt=""/>
				</div>
				<div>
					<img class="myCheese" src="https://f.wishabi.net/creative/Distribution/images/icons/myCheese.svg" alt=""/>
				</div>
				<div>
					<img class="myBread" src="https://f.wishabi.net/creative/Distribution/images/icons/myBread.svg" alt=""/>
				</div>
				<div>
					<img class="myApple" src="https://f.wishabi.net/creative/Distribution/images/icons/myApple.svg" alt=""/>
				</div>
				<div>
					<img class="myBroccoli" src="https://f.wishabi.net/creative/Distribution/images/icons/myBroccoli.svg" alt=""/>
				</div>
				<div>
					<img class="myYogurt" src="https://f.wishabi.net/creative/Distribution/images/icons/myYogurt.svg" alt=""/>
				</div>
			</div>
		);

		const flyersContent = (
			<div class="wrapper-left">
				<article id="primary-content-flyers-article" classList={{"mobile": this.userAgent.mobile}}>
					<div class="article-content" classList={{"mobile": this.userAgent.mobile}}>
						<div class="feature-heading" classList={{"french": isFrench}}>
							<I18nSpan key="flyers_feature_heading" values={JSON.stringify({1: "emphasize separate"})} />
						</div>
						<div class="description">
							<p>{i18n("flyers_feature_description1")}</p>
							<p>{i18n("flyers_feature_description2_part1")}
							<span class="flipp-app-link">
								<a href="https://flipp.app.link/KYJFGWvFo1">{i18n("flipp_app_title")}</a>
							</span>{i18n("flyers_feature_description2_part2")}
							</p>
						</div>
						<div class="logos-wrapper">
							{retailerLogos}
							<div class="cta">
								<button class="flipp-button" onClick={this._doBrowseFlyers}>{i18n("flyers_feature_cta") }</button>
							</div>
						</div>
					</div>
					<picture >
						<source type="image/webp"  media="(max-width: 600px) " srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/flyers-${countrySuffix}${langSuffix}-mobile.webp`} />
						<source type="image/jpg"  media="(max-width: 600px)" srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/flyers-${countrySuffix}${langSuffix}-mobile.jpg`} />
						<source type="image/webp" srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/flyers-${countrySuffix}${langSuffix}.webp`} />
						<img classList={{"mobile": this.userAgent.mobile, "french": isFrench}} src={`https://f.wishabi.net/creative/Distribution/images/landing-v2/flyers-${countrySuffix}${langSuffix}.jpg`} alt="image of flipp.com flyers page" />
					</picture>
				</article>
			</div>
		);

		const couponsContent = (this.location.locale !== "en-ca" && this.location.locale !== "fr-ca" ?
			<div class="wrapper-right">
				<article id="primary-content-coupons-article" classList={{"mobile": this.userAgent.mobile}}>
					<picture>
						<source type="image/webp"  media="(max-width: 600px) " srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/coupons-${countrySuffix}${langSuffix}-mobile.webp`} />
						<source type="image/jpg"  media="(max-width: 600px)" srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/coupons-${countrySuffix}${langSuffix}-mobile.jpg`} />
						<source type="image/webp" srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/coupons-${countrySuffix}${langSuffix}.webp`} />
						<img classList={{"mobile": this.userAgent.mobile, "french": isFrench}} src={`https://f.wishabi.net/creative/Distribution/images/landing-v2/coupons-${countrySuffix}${langSuffix}.jpg`} alt="image of flipp.com coupons page" />
					</picture>

					<div class="article-content" classList={{"mobile": this.userAgent.mobile}}>
						<div class="feature-heading" classList={{"french": isFrench}}>
							<I18nSpan key="coupons_feature_heading" values={JSON.stringify({1: "emphasize"})} />
						</div>
						<div class="description">
							<p>{i18n("coupons_feature_description1")}</p>
							<p>{i18n("coupons_feature_description2_part1")}
							<span class="flipp-app-link">
								<a href="https://flipp.app.link/bzQgNIHdlZ">Flipp app</a>
							</span>
								{i18n("coupons_feature_description2_part2")}
							</p>
						</div>
						<div class="logos-wrapper">
							{cpgLogos}
							<div class="cta">
								<button class="flipp-button" onClick={this._doBrowseCoupons}>{i18n("coupons_feature_cta") }</button>
							</div>
						</div>
					</div>
				</article>
			</div>
				:
				null
		);

		const slContent = (
			<div class="wrapper-left">
				<article id="primary-content-sl-article" class="sl" classList={{"mobile": this.userAgent.mobile}}>
					<div class="article-content sl" classList={{"mobile": this.userAgent.mobile}}>
						<div class="feature-heading" classList={{"french": isFrench}}>
							<I18nSpan key="sl_feature_heading" values={JSON.stringify({1: "emphasize"})} />
						</div>
						<div class="description">
							<p>{i18n("sl_feature_description_part1")}
								<span class="flipp-app-link"><a href="https://flipp.app.link/bzQgNIHdlZ">{i18n("flipp_app_title")}</a></span>
								{i18n("sl_feature_description_part2")}
							</p>
						</div>
						<div class="happy-fruit-wrapper" classList={{"mobile": this.userAgent.mobile}}>
							{happyFruit}
							<div class="cta">
								<button class="flipp-button" onClick={this._doCreateSL}>{i18n("sl_feature_cta") }</button>
							</div>
						</div>
					</div>
					<picture>
						<source type="image/webp"  media="(max-width: 600px) " srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/sl-${countrySuffix}${langSuffix}-mobile.webp`} />
						<source type="image/jpg"  media="(max-width: 600px)" srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/sl-${countrySuffix}${langSuffix}-mobile.jpg`} />
						<source type="image/webp" srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/flyers-${countrySuffix}${langSuffix}.webp`} />
						<img class="sl-image" classList={{"mobile": this.userAgent.mobile, "french": isFrench}} src={`https://f.wishabi.net/creative/Distribution/images/landing-v2/sl-${countrySuffix}${langSuffix}.jpg`} alt="image of flipp.com shopping list page" />
					</picture>
				</article>
			</div>
		);

		const primaryContent = (
			<section class="primary-content" classList={{"mobile": this.userAgent.mobile}}>
				{flyersContent}
				{couponsContent}
				{slContent}
			</section>
		);

		const searchSection = (
			<section id="search-section" class="search" classList={{"mobile": this.userAgent.mobile}}>
				<h2>
					<I18nSpan key="search_value" values={JSON.stringify({1: "emphasize"})} />
				</h2>
				<div class="searchbox" classList={{"french": isFrench}}>
					<form onSubmit={this._handleSearchSubmit}>
						<input
							ref="searchInput"
							type="text"
							placeholder={this.userAgent.mobile ? i18n("search_flipp") : i18n("deal_search_desktop")} />
						<button class="flipp-button" type="submit">{i18n("search_flipp")}</button>
					</form>
				</div>
			</section>
		);

		const mobileAppSection = (
			<div class="mobile-app-wrapper">
			<section class="mobile-app" classList={{"mobile": this.userAgent.mobile}}>
				<article>
					<div class="article-content" classList={{"mobile": this.userAgent.mobile}}>
						<div class="feature-heading" classList={{"french": isFrench}}>
							<I18nSpan key="mobile_title_new" values={JSON.stringify({1: "emphasize"})} />
						</div>
						<div class="description">
							<p>{i18n("mobile_value")}</p>
						</div>
						<div class="mobile-features">
							<div class="button-area">

								{!this.userAgent.mobile || this.userAgent.mobile && this.userAgent.iOS
								?
									<a href="https://flipp.app.link/mbl75DoBBab" target="_blank" onClick={this._handleInstallClick.bind(this, "ios")}>
										<img alt="image of iOS App store" src={`https://f.wishabi.net/creative/Distribution/images/ios-appstore-${lang}.png`} />
									</a>
								: null}

								{!this.userAgent.mobile || this.userAgent.mobile && this.userAgent.android
								?
									<a href="https://flipp.app.link/mbl75DoBBab"
										target="_blank"
										onClick={this._handleInstallClick.bind(this, "android")}
									>
										<img alt="image of Google Play store" src={`https://f.wishabi.net/creative/Distribution/images/android-appstore-${lang}.png`} />
									</a>
								: null}

							</div>
						</div>
					</div>
					<img classList={{"mobile": this.userAgent.mobile}}
						src={this.location.locale === "fr-ca" ?
							"https://f.wishabi.net/creative/Distribution/images/homepage/app-fr.png"
							:
							`https://f.wishabi.net/creative/Distribution/images/homepage/app-${countrySuffix}${langSuffix}.png`}
						alt="image of a phone with Flipp App displayed"
					/>
				</article>
			</section>
			</div>
		);


		const footerBlock = (
			<footer>
				{isCrawler() && (
						<ul>
							<li><a href={hostname + `/en-ca`} target="_blank" >Canada</a></li>
							<li><a href={hostname + `/en-us`} target="_blank" >USA</a></li>
						</ul>
					)}
				<button class="back-to-top" aria-label={i18n("back_to_top")} onClick={this._handleBackToTopClick}>{icons.backArrow}</button>
				<div class="container">
					<div>
						<a href={FLIPP_CORP_BASEPATH} class="footer-logo" target="_blank"
							onClick={this._handleAboutFlippClick.bind(this, "Footer Logo")}>
							{icons.flippLogo}
						</a>
					</div>
					<div class="links">
						<FlippCitiesFooterLinks />
						<ul>
							<li><a href={FLIPP_CORP_BASEPATH + "/index"} target="_blank" >{i18n("retail_platform")}</a></li>
							<li><a href={FLIPP_CORP_BASEPATH + "/media"} target="_blank" >{i18n("media_platform")}</a></li>
							<li><a href={this.userAgent.getAppLinkHref("main_landing_page_footer", "landing_page")} target="_blank" onClick={this._handleAppLinkClick}>{i18n("mobile_app")}</a></li>
							<li><a href={FLIPP_CORP_BASEPATH + "/about"} target="_blank" >{i18n("about_us")}</a></li>
							<li><a href={FLIPP_CORP_BASEPATH + "/contact"} target="_blank" >{i18n("contact_us")}</a></li>
							<li><a href={i18n("help_link")} target="_blank" >{i18n("help")}</a></li>
						</ul>

						<ul>
							<li><a href={FLIPP_CORP_BASEPATH + "/careers"} target="_blank" onClick={this._handleJobOpeningsLinkClick}>{i18n("careers")}</a></li>
							<li><a href="https://merchants.wishabi.com" target="_blank">{i18n("client_login")}</a></li>
							<li><a href={FLIPP_CORP_BASEPATH + "/privacy"} target="_blank" >{i18n("privacy_policy")}</a></li>
							<li><a href={FLIPP_CORP_BASEPATH + "/terms"} target="_blank" >{i18n("terms")}</a></li>
							<li><a href={FLIPP_CORP_BASEPATH + "/images/flipp_corp/Multi-yearAccessibilityPlan-Flipp.pdf"} target="_blank">{i18n("accessibility")}</a></li>
							<li><a href="https://corp.flipp.com/privacy#INFO55" target="_blank">{i18n("ad_choices")}</a></li>
						</ul>
					</div>

					<div class="jobs-wrapper">
					<div class="jobs">
						<p>{i18n("blog_desc")}</p>
						<p><a class="flipp-button" target="_blank" href="http://blog.flipp.com/?utm_source=Flipp%20Web&utm_medium=Organic&utm_campaign=Blog%20Home"
							onClick={this._handleJobOpeningsButtonClick}>
							{i18n("read_blog")}
							</a>
						</p>
					</div>
					</div>
				</div>
				<div class="separator"></div>
				<div class="copyright">
					<div class="getsocial">
						<a href="https://www.facebook.com/getflipp" target="_blank" onClick={this._handleFacebookButtonClick}>
							<img src="https://f.wishabi.net/creative/Distribution/images/landing/social/fb.png" class="fb" width="24" />
						</a>
						<a href="https://www.instagram.com/getflipp" target="_blank" onClick={this._handleFacebookButtonClick}>
							<img src="https://f.wishabi.net/creative/Distribution/images/homepage/Instagram.png" class="insta" width="20" />
						</a>
						<a href="https://www.linkedin.com/company/flipp/" target="_blank"
							onClick={this._handleLinkedinButtonClick}>
							<img src="https://f.wishabi.net/creative/Distribution/images/landing/social/linkedin.png" class="linkedin" height="17" />
						</a>
					</div>
					<div>{i18n("copyright", { year: String(year) })}</div>
				</div>
			</footer>
		);

		this.seoService.insertLoadingMarker(100);
		return (
			<main>
				<MobileBanner />
				{headerBlock}
				{this.getHeroSection()}
				{primaryContent}
				{searchSection}
				{mobileAppSection}
				{footerBlock}
			</main>
		);
	}

	private getBlackFridayHeroSection = () => {
		const imgPrefix = this.userAgent.mobile ? "_mobile" : "";
		const imgSrc = `https://f.wishabi.net/creative/Distribution/images/bf/landingpage${imgPrefix}.png`;
		return (
			<section class="hero hero--bf">
				<img
						class="bf__img"
						src={imgSrc}
						alt={i18n("blackfriday_home_banner")} />

				<div class="bf__postal">
					<div class="bf__title">
						{i18n("blackfriday_home_wording")}
					</div>
					<div class="postal-code" ref="postalCodeWrapper">
						<form ref="postalCodeForm"
									action={this.linkService.linkTo("redirect")}
									onSubmit={this._handlePostalCodeSubmit}>
							<div class="message">{i18n("enter_postal_code_error_message") }</div>
							<div class="input-wrapper">
								<input type="hidden" name="page" value="listing" />
								<input type="hidden" name="old_fsa" value={this.location.postalCode} />
								<input
									ref="postalCodeInput"
									class="content-input"
									type="text"
									placeholder={this.location.postalCode}
									name="postal_code"
									autocomplete="off"
									onBlur={this._postalCodeBlur}
									onInput={this._postalCodeInput}
									maxlength="7" />

								<button class="flipp-button">{i18n("start_saving") }</button>
							</div>
						</form>
					</div>
				</div>
			</section>
		);
	}

	private getHeroSection = () => {
		console.log(this.bfService.isEnabled());
		if (this.bfService.isEnabled()) {
			return this.getBlackFridayHeroSection();
		}
		const postalCodeGuess = this.location.postalCode;
		const countrySuffix = this.location.country === Country.CANADA ? "ca" : "us";
		const langSuffix = this.location.acceptsLocale("fr") ? "-fr" : "";

		return (
			<section class="hero">
				<div class="wrapper-left">
					<div class="hero-features">
						<h1 classList={{"french": this.location.acceptsLocale("fr"), "mobile": this.userAgent.mobile}}>
							<I18nSpan key="index_page_h1" values={JSON.stringify({1: "emphasize colorful"})} />
						</h1>
						<div class="summary">
							<h2><I18nSpan key="how_to_save_feature1" values={JSON.stringify({1: "emphasize"})} /></h2>
							<div class="postal-code" ref="postalCodeWrapper">
								<form ref="postalCodeForm"
											action={this.linkService.linkTo("redirect")}
											onSubmit={this._handlePostalCodeSubmit}>
									<div class="message">{i18n("enter_postal_code_error_message") }</div>
									<div class="input-wrapper">
										<input type="hidden" name="page" value="listing" />
										<input type="hidden" name="old_fsa" value={postalCodeGuess} />
										<input
											ref="postalCodeInput"
											class="content-input"
											type="text"
											placeholder={postalCodeGuess}
											name="postal_code"
											autocomplete="off"
											onBlur={this._postalCodeBlur}
											onInput={this._postalCodeInput}
											maxlength="7" />

										<button class="flipp-button">{i18n("start_saving") }</button>
									</div>
								</form>
							</div>
						</div>
					</div>
				</div>
				<div >
					<picture class="hero-image">

						<source type="image/webp"  media="(max-width: 600px)" srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/hero-${countrySuffix}${langSuffix}-mobile.webp`} />
						<source type="image/jpg"  media="(max-width: 600px)" srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/hero-${countrySuffix}${langSuffix}-mobile.jpg`} />
						<source type="image/webp" srcSet={`https://f.wishabi.net/creative/Distribution/images/landing-v2/hero-${countrySuffix}${langSuffix}.webp`} />
						<img src={`https://f.wishabi.net/creative/Distribution/images/landing-v2/hero-${countrySuffix}${langSuffix}.jpg`}  alt="image of a laptop with flipp.com page loaded on the screen" />
					</picture>
				</div>
			</section>
		);
	}

	private _getSecondaryNav = () => {
		const country = this.location.country;
		const isFrench = this.location.acceptsLocale("fr");

		const langLink = (country === Country.CANADA && isFrench
			? <a href="/home?locale=en-ca" onClick={this._setLocale.bind(this, "en-ca")}>English</a>
			: country === Country.CANADA && !isFrench
				? <a href="/accueil?locale=fr-ca" onClick={this._setLocale.bind(this, "fr-ca")}>Français</a>
				: null
		);

		const secondaryNav = (
			<div class="secondary-nav">
				{langLink}
				<a href={FLIPP_CORP_BASEPATH} target="_blank" onClick={this._handleAboutFlippClick.bind(this, "Nav Text Link")}>
					{i18n("about_flipp")}
				</a>
				<a href={this.userAgent.getAppLinkHref("main_landing_page_top_bar", "landing_page")} target="_blank" onClick={this._handleAppLinkClick}>
					{i18n("flipp_mobile")}
				</a>
				<a href="http://blog.flipp.com/?utm_source=Flipp%20Web&utm_medium=Organic&utm_campaign=Blog%20Home">{i18n("blog_button")}</a>
			</div>
		);

		return secondaryNav;
	}

	private _handleInstallClick = (device: string, e: Event) => {
		this.analytics.trackMobileInstall(device, "Landing Page");
	}

	private _getPostalCodeValue() {
		const input = this.refs.postalCodeInput;
		let value = input.value;
		if (value === "") {
			value = this.location.postalCode;
		}
		return value.replace(" ", "");
	}

	private _postalCodeBlur = (e: Event) => {
		e.preventDefault();

		const value = this._getPostalCodeValue();
		if (!PostalCode.isValid(value)) {
			this.refs.postalCodeWrapper.classList.add("invalid");
			this.refs.postalCodeForm.classList.add("invalid");
		} else {
			this.refs.postalCodeWrapper.classList.remove("invalid");
			this.refs.postalCodeForm.classList.remove("invalid");
		}
	}

	private _postalCodeInput = (e: Event) => {
		e.preventDefault();

		const value = this._getPostalCodeValue();
		if (PostalCode.isValid(value)) {
			this.refs.postalCodeWrapper.classList.remove("invalid");
			this.refs.postalCodeForm.classList.remove("invalid");
		}
	}

	private _handlePostalCodeSubmit = (e: Event) => {
		const linkService = LinkService.getInstance();
		const value = this._getPostalCodeValue();
		if (!PostalCode.isValid(value)) {
			this.refs.postalCodeWrapper.classList.add("invalid");
			this.refs.postalCodeForm.classList.add("invalid");
			e.preventDefault();
			return;
		}

		if (this.location.postalCode === value) {
			e.preventDefault();

			// Navigate to the listing route if postal code is the same
			linkService.execute(linkService.linkTo("listing", {
				postal_code: value
			}), this.refs.postalCodeForm);
		}

		this.analytics.trackLandingPage(LandingPageActions.StartSavingButtonClick, value);

		// On form submit, spaces are encoded as '+' characters which breaks us
		// Manually clean up the value by stripping any whitespace
		const input = this.refs.postalCodeInput;
		input.value = value.replace(" ", "");

		// FIXME [FW-281]: Use database as injection when it's ready
		/* User entered a postal code but it's different than the postal code 'guess'
			* We've already loaded flyers based on the 'guess' postal code, so they
			* need to be cleared and then we need to fetch the proper flyers. Navigate
			* with a full page transition as it has the same effect
			*/
		// do nothing & let the form submit.
	}

	private _handleSearchSubmit = (e: Event) => {
		e.preventDefault();
		const input = this.refs.searchInput;
		if (input.value === "") {
			return;
		}
		this._doSearch(input.value, input.parentElement);
	}

	private _doSearch = (queryStr: string, parentElem: HTMLElement) => {
		// FIXME [FW-400] We should be reusing the <SearchBox> component.
		const linkService = LinkService.getInstance();
		linkService.execute(linkService.linkTo("search", {
			q: queryStr
		}), this);

		this.analytics.trackLandingPage(LandingPageActions.SearchFlippButtonClick, queryStr);
	}

	private _doBrowseFlyers = (e: Event) => {
		e.preventDefault();
		const linkService = LinkService.getInstance();
		linkService.execute(linkService.linkTo("listing"), this);

		this.analytics.trackLandingPage(LandingPageActions.BrowseFlyersButtonClick);
	}

	private _doBrowseCoupons = (e: Event) => {
		e.preventDefault();
		const linkService = LinkService.getInstance();
		linkService.execute(linkService.linkTo("coupons"), this);

		this.analytics.trackLandingPage(LandingPageActions.BrowseCouponsButtonClick);
	}

	private _doCreateSL = (e: Event) => {
		e.preventDefault();
		const linkService = LinkService.getInstance();
		linkService.execute(linkService.linkTo("shopping_list"), this);

		this.analytics.trackLandingPage(LandingPageActions.CreateSLButtonClick);
	}

	private _handleAppLinkClick = (e: Event) => {
		const category = "Landing Page";
		let device = "desktop (mobile app link)";

		if (this.userAgent.android) {
			device = "android (mobile app link)";
		} else if (this.userAgent.iOS) {
			device = "ios (mobile app link)";
		}

		this.analytics.trackMobileInstall(device, category);
	}

	private _handleJobOpeningsButtonClick = (e: Event) => {
		this.analytics.trackLandingPage(LandingPageActions.JobOpeningsButtonClick);
	}

	private _handleJobOpeningsLinkClick = (e: Event) => {
		this.analytics.trackLandingPage(LandingPageActions.JobOpeningsLinkClick);
	}

	private _handleFacebookButtonClick = (e: Event) => {
		this.analytics.trackLandingPage(LandingPageActions.FacebookButtonClick);
	}

	private _handleTwitterButtonClick = (e: Event) => {
		this.analytics.trackLandingPage(LandingPageActions.TwitterButtonClick);
	}

	private _handleYoutubeButtonClick = (e: Event) => {
		this.analytics.trackLandingPage(LandingPageActions.YoutubeButtonClick);
	}

	private _handleLinkedinButtonClick = (e: Event) => {
		this.analytics.trackLandingPage(LandingPageActions.LinkedinButtonClick);
	}

	private _handleFlyersNavClick = (e: Event) => {
		window.scrollBy(0, 600);
		this.analytics.trackLandingPage(LandingPageActions.FlyersNavClick);
	}

	private _handleCouponsNavClick = (e: Event) => {
		window.scrollBy(0, 1300);
		this.analytics.trackLandingPage(LandingPageActions.CouponsNavClick);
	}

	private _handleSLNavClick = (e: Event) => {
		window.scrollBy(0, 1950);
		this.analytics.trackLandingPage(LandingPageActions.SLNavClick);
	}

	private _handleSearchNavClick = (e: Event) => {
		window.scrollBy(0, 2600);
		this.analytics.trackLandingPage(LandingPageActions.SearchNavClick);
	}

	private _handleBackToTopClick = (e: Event) => {
		window.scrollTo(0, 0);
		this.analytics.trackLandingPage(LandingPageActions.BackToTopClick);
	}

	private _handleAboutFlippClick = (location: string, e: Event) => {
		this.analytics.trackLandingPage(LandingPageActions.AboutFlippClick, location);
	}

	private _setLocale = (lang: string) => {
		this.storage.setItem("locale", lang);
	}

	private _getNavDialog = () => {
		if (!this.navDialog) {
			this.navDialog = <Dialog class="nav-dialog" transient>
					<div flex-vertical>
						<button class="close" onClick={this._closeNavDialog}>{icons.x}</button>
						<div class="flipp-logo">
							<a href={FLIPP_CORP_BASEPATH} target="_blank" onClick={this._handleAboutFlippClick.bind(this, "Nav Logo")}>
								{icons.flippLogo}
							</a>
						</div>
						{this._getSecondaryNav()}
					</div>
				</Dialog> as Dialog;
		}
		return this.navDialog;
	}

	private _closeNavDialog = () => {
		this._getNavDialog().close();
	}


	private _handleHamburgerClick = (e: Event) => {
		this._getNavDialog().showModal();
	}
}
